import { useState } from 'react'
import { FormSelect } from '@/components/ui/FormField'
import type { UseFormReturn } from 'react-hook-form'
import type { InviteeFormSchemaType } from '../utils/inviteeValidators'
import { CoApplicantForm } from './CoApplicantForm'
import { useFieldArray } from 'react-hook-form'
import styles from '../styles/inviteeAdd.module.css'

interface CoApplicantSectionProps {
  form: UseFormReturn<InviteeFormSchemaType>
  onCheckDuplicate?: (index: number) => void
}

export function CoApplicantSection({ form, onCheckDuplicate }: CoApplicantSectionProps) {
  const { control } = form

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'co_applicant',
  })

  const [coApplicantCount, setCoApplicantCount] = useState(0)

  const handleCountChange = (count: number) => {
    setCoApplicantCount(count)
    const currentCount = fields.length

    if (count > currentCount) {
      // Add new co-applicants
      for (let i = currentCount; i < count; i++) {
        append({
          first_name: '',
          last_name: '',
          email: '',
          mobile_no: '',
          relationship_type: '' as any,
          living_unit: undefined as any,
          is_approved_require: undefined as any,
          income_verified: '',
          income: '',
        }, { shouldFocus: false })
      }
    } else if (count < currentCount) {
      // Remove co-applicants
      for (let i = currentCount - 1; i >= count; i--) {
        remove(i)
      }
    }
  }

  return (
    <>
      <h2
        data-testid="invitee-add-section-co-applicant-title"
        className={styles.sectionTitle}
      >
        Co-Applicant
      </h2>

      {/* Note */}
      <div className={styles.formRow}>
        <div className={`${styles.formColWide} ${styles.formColWideOffset}`}>
          <div
            data-testid="invitee-add-co-applicant-privacy-note"
            className={styles.privacyNote}
          >
            Note: Please identify all co-applicants, who will reside in the unit. We require all names to be listed on
            our Liberty Rent policy. However, some may not need our additional underwriting, if they passed your
            credit screening individually. Please mark co-applicants who are not required to apply with us as NO
            for Liberty approval needed.
          </div>
        </div>
      </div>

      {/* Co-Applicant Count */}
      <div
        data-testid="invitee-add-co-applicant-count-row"
        className={styles.formRow}
      >
        <div className={`${styles.formColWide} ${styles.formColWideOffset}`}>
          <div className={`${styles.formGroup} ${styles.coApplicantCountGroup}`}>
            <label
              data-testid="invitee-add-co-applicant-count-label"
              className={`${styles.formLabel} ${styles.coApplicantCountLabel}`}
            >
              How many Co-Applicants will be on the Lease?
            </label>
            <div className={styles.coApplicantCountSelect}>
              <FormSelect
                data-testid="invitee-add-co-applicant-count-select"
                value={coApplicantCount.toString()}
                onChange={(e) => handleCountChange(parseInt(e.target.value, 10))}
                className={styles.customFormControl}
              >
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
              </FormSelect>
            </div>
          </div>
        </div>
      </div>

      {/* Co-Applicant Forms */}
      {fields.map((field, index) => (
        <div
          key={field.id}
          data-testid={`invitee-add-co-applicant-card-row-${index}`}
          className={styles.coApplicantCardRow}
        >
          <div className={styles.coApplicantCardWrap}>
            <div
              data-testid={`invitee-add-co-applicant-card-${index}`}
              className={styles.coApplicantCard}
            >
              <CoApplicantForm
                form={form}
                index={index}
                onRemove={() => {
                  remove(index)
                  setCoApplicantCount(fields.length - 1)
                }}
                onCheckDuplicate={() => onCheckDuplicate && onCheckDuplicate(index)}
              />
            </div>
          </div>
        </div>
      ))}
    </>
  )
}
